import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Tippy from "@tippyjs/react";
import "tippy.js/animations/scale.css";
import "tippy.js/dist/tippy.css";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import SpotifyVisualizer from "../components/SpotifyVisualizer";
import SpotifyAvatarRing from "../components/SpotifyAvatarRing";

function CustomStatus({ customStatus }) {
  return (
    <>
      {customStatus && (
        <p className="text-xs text-slate-700 dark:text-slate-300 font-medium break-all mt-0.5">
          ▸ {customStatus.emoji && <span className="mr-1">{customStatus.emoji.name}</span>}
          {customStatus.state}
        </p>
      )}
    </>
  );
}

function UserInfo() {
  const userId = "655981892193091594";

  const [userData, setUserData] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isWeather, setIsWeather] = useState(false);
  const [weather, setWeather] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Avatar decoration (frame)
  const [avatarFrame, setAvatarFrame] = useState(null);

  // Preview modal
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const fetchData = async () => {
    try {
      const response = await axios.get(`https://api.lanyard.rest/v1/users/${userId}`);
      setUserData(response.data.data);
    } catch (error) {
      console.error("Lỗi:", error);
    }
  };

  const fetchWeather = async () => {
    try {
      const response = await axios.get(
        "https://api.openweathermap.org/data/2.5/weather?q=Ho%20Chi%20Minh,vn&appid=a601622a383aee1aea5573743d8e8875&units=metric"
      );
      setWeather(response.data);
    } catch (error) {
      console.error("Lỗi:", error);
    }
  };

  const fetchAvatarFrame = async () => {
    try {
      const res = await fetch(`https://discord-lookup-api-alpha.vercel.app/v1/user/${userId}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();

      if (data?.avatar_decoration?.asset) {
        setAvatarFrame(`https://cdn.discordapp.com/avatar-decoration-presets/${data.avatar_decoration.asset}`);
      } else {
        setAvatarFrame(null);
      }
    } catch (error) {
      console.error("Lỗi:", error);
      setAvatarFrame(null);
    }
  };

  useEffect(() => {
    fetchData();
    fetchWeather();
    fetchAvatarFrame();

    const intervalId = setInterval(() => {
      fetchData();
      fetchWeather();
      fetchAvatarFrame();
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const startTimestamp = userData?.activities?.find((a) => a.type === 0)?.timestamps?.start;
    if (!startTimestamp) return;

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTimestamp;
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [userData]);

  const formatElapsedTime = (milliseconds) => {
    const seconds = Math.floor((milliseconds || 0) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m ${seconds % 60}s`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  if (!userData || !weather) {
    return (
      <>
        <div className="md:flex gap-4 sm:gap-5 items-center">
          <div className="md:m-0 mb-4 mx-auto rounded-full min-w-24 size-24 sm:min-w-28 sm:size-28 overflow-hidden border-4 border-slate-700">
            <div className="rounded-full overflow-hidden border-4 border-transparent">
              <div className="size-[84px] sm:size-[100px] bg-slate-300 animate-pulse"></div>
            </div>
          </div>
          <div className="text-gray-900 dark:text-gray-100">
            <h2 className="font-semibold text-xl sm:text-2xl">
              Hey, tôi là{" "}
              <Tippy animation="scale" content="hienleeone">
                <span className="text-slate-700 dark:text-slate-200">SoulLab_</span>
              </Tippy>{" "}
              👋
            </h2>
            <div className="w-full h-1 bg-slate-700 rounded-sm my-1"></div>
            <div className="font-normal text-justify text-slate-600 dark:text-slate-300 leading-relaxed mt-1.5 text-xs sm:text-sm">
              Xin chào! Tôi là <span className="text-slate-900 dark:text-white font-semibold">SoulLab_ (2002)</span> - đang tập trung quay trở lại và hướng tới chuyên ngành công nghệ đã học sau một thời gian trải nghiệm các công việc khác nhau. Song song đó, tôi dành thời gian tự học lập trình và phát triển các dự án game cộng đồng, định hướng ứng dụng kiến thức bảo mật để cải thiện trải nghiệm cho người chơi.{" "}
              <Link className="text-indigo-500 dark:text-indigo-400 hover:underline font-semibold" to="/skills">
                Xem thêm?
              </Link>{" "}
              ✒️
            </div>
          </div>
        </div>
        <div className="w-52 rounded-full bg-slate-400 dark:bg-slate-700 animate-pulse h-3.5 mt-2.5"></div>
        <div className="w-44 rounded-full bg-slate-400 dark:bg-slate-700 animate-pulse h-3.5 mt-2"></div>
      </>
    );
  }

  const { discord_user, activities = [], discord_status } = userData;
  const customStatus = activities.find((activity) => activity.type === 4);


  // STATUS UI (khôi phục đầy đủ)
  const online = (
    <div className="flex items-center">
      <div className="translate-y-[-1px] size-3 rounded-full bg-green-500">
        <div className="size-3 rounded-full bg-green-500 animate-ping"></div>
      </div>
      <div
        className="relative ml-2 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <p>Đang Online</p>
        {isHovered && (
          <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[200px] max-w-xs px-3 py-2 rounded-lg bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border border-slate-300/80 dark:border-slate-700 shadow-xl text-xs space-y-1 select-none pointer-events-none">
            <p className="font-bold text-slate-900 dark:text-white text-xs">☕ Hãy nhắn tin cho tôi!</p>
            {activities.length > 0 &&
              activities.map((activity) => (
                <div key={`${activity?.id || activity?.name || "act"}-${activity?.type}`}>
                  {activity.type === 0 && (
                    <p className="text-slate-800 dark:text-slate-200">
                      ▸ Đang chơi 🌠: {activity.name}{" "}
                      <span className="text-[11px] text-slate-600 dark:text-slate-400 font-bold">({formatElapsedTime(elapsedTime)})</span>
                    </p>
                  )}
                </div>
              ))}

            <CustomStatus customStatus={customStatus} />
          </div>
        )}
      </div>
    </div>
  );

  const idle = (
    <div className="flex items-center">
      <div className="translate-y-[-1px] size-3 rounded-full bg-yellow-500">
        <div className="size-3 rounded-full bg-yellow-500 animate-ping"></div>
      </div>
      <div
        className="relative ml-2 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <p>Treo máy</p>
        {isHovered && (
          <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[180px] max-w-xs px-3 py-2 rounded-lg bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border border-slate-300/80 dark:border-slate-700 shadow-xl text-xs space-y-1 select-none pointer-events-none">
            <p className="font-bold text-slate-900 dark:text-white text-xs">🧩 Tôi đang bận!</p>
            <CustomStatus customStatus={customStatus} />
          </div>
        )}
      </div>
    </div>
  );

  const offline = (
    <div className="flex items-center">
      <div className="translate-y-[-1px] size-3 rounded-full bg-red-600">
        <div className="size-3 rounded-full bg-red-600 animate-ping"></div>
      </div>
      <div
        className="relative ml-2 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <p>Đang Offline</p>
        {isHovered && (
          <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[180px] max-w-xs px-3 py-2 rounded-lg bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border border-slate-300/80 dark:border-slate-700 shadow-xl text-xs space-y-1 select-none pointer-events-none">
            <p className="font-bold text-slate-900 dark:text-white text-xs">💤 Tìm tôi sau nhé!</p>
            <CustomStatus customStatus={customStatus} />
          </div>
        )}
      </div>
    </div>
  );

  const dnd = (
    <div className="flex items-center">
      <div className="translate-y-[-1px] size-3 rounded-full bg-red-900">
        <div className="size-3 rounded-full bg-red-900 animate-ping"></div>
      </div>
      <div
        className="relative ml-2 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <p>Đừng làm phiền</p>
        {isHovered && (
          <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[180px] max-w-xs px-3 py-2 rounded-lg bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border border-slate-300/80 dark:border-slate-700 shadow-xl text-xs space-y-1 select-none pointer-events-none">
            <p className="font-bold text-slate-900 dark:text-white text-xs">🚫 Xin đừng làm phiền!</p>
            <CustomStatus customStatus={customStatus} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Left Column: User Info */}
      <div className="w-full">
        <div className="md:flex gap-4 sm:gap-5 items-center">
          <div
            className="relative md:m-0 mb-4 mx-auto min-w-24 size-24 sm:min-w-28 sm:size-28 cursor-pointer z-30"
            onClick={() => setIsPreviewOpen(true)}
            role="button"
            aria-label="Open avatar preview"
          >
            {/* Spotify Animation Ring */}
            <SpotifyAvatarRing isPlaying={!!userData?.spotify} radius={58} />

            {/* Avatar image */}
            <img
              className="rounded-full w-full h-full object-cover border-4 border-slate-700 relative z-10"
              src={`https://cdn.discordapp.com/avatars/${discord_user.id}/${discord_user.avatar}.png?size=256`}
              alt="Avatar"
            />
            {/* Avatar frame overlay */}
            {avatarFrame && (
              <img
                src={avatarFrame}
                alt="Avatar Frame"
                className="absolute inset-0 w-full h-full rounded-full z-20 pointer-events-none"
              />
            )}
          </div>

          <div className="text-gray-900 dark:text-gray-100">
            <h2 className="font-semibold text-xl sm:text-2xl">
              Hey, tôi là{" "}
              <Tippy animation="scale" content="hienleeone">
                <span className="text-slate-700 dark:text-slate-200">SoulLab_</span>
              </Tippy>{" "}
              👋
            </h2>
            <div className="w-full h-1 bg-slate-700 rounded-sm my-1"></div>
            <div className="font-normal text-justify text-slate-600 dark:text-slate-300 leading-relaxed mt-1.5 text-xs sm:text-sm">
              Xin chào! Tôi là <span className="text-slate-900 dark:text-white font-semibold">SoulLab_ (2002)</span> - đang tập trung quay trở lại và hướng tới chuyên ngành công nghệ đã học sau một thời gian trải nghiệm các công việc khác nhau. Song song đó, tôi dành thời gian tự học lập trình và phát triển các dự án game cộng đồng, định hướng ứng dụng kiến thức bảo mật để cải thiện trải nghiệm cho người chơi.{" "}
              <Link className="text-indigo-500 dark:text-indigo-400 hover:underline font-semibold" to="/skills">
                Xem thêm?
              </Link>{" "}
              ✎
            </div>
          </div>
        </div>

        {/* status line */}
        <div className="font-semibold text-gray-900 dark:text-gray-100 mt-2.5 text-xs sm:text-sm">
          {discord_status === "online" ? online : discord_status === "idle" ? idle : discord_status === "dnd" ? dnd : offline}
        </div>

        {/* location + weather hover */}
        <div className="text-xs sm:text-sm">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 text-gray-800 dark:text-gray-200 -translate-x-[2px] -translate-y-[1px]"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                fillRule="evenodd"
                d="M12 2a8 8 0 0 1 6.6 12.6l-.1.1-.6.7-5.1 6.2a1 1 0 0 1-1.6 0L6 15.3l-.3-.4-.2-.2v-.2A8 8 0 0 1 11.8 2Zm3 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                clipRule="evenodd"
              />
            </svg>
            <div
              className="relative font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
              onMouseEnter={() => setIsWeather(true)}
              onMouseLeave={() => setIsWeather(false)}
            >
              <p>TP.HCM, Vietnam</p>
              {isWeather && weather && (
                <div className="absolute left-0 top-full mt-1.5 z-50 flex items-center gap-2.5 px-3 py-1.5 rounded-lg bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border border-slate-300/80 dark:border-slate-700 shadow-xl whitespace-nowrap text-xs select-none pointer-events-none">
                  <span className="text-slate-800 dark:text-slate-200 font-bold">⛺ {weather?.name}</span>
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  <span className="text-slate-700 dark:text-slate-300 font-semibold">⛅ {Math.round(weather?.main?.temp)}°C</span>
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  <span className="text-slate-700 dark:text-slate-300 font-semibold">🚿 {weather?.main?.humidity}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Spotify */}
      <AnimatePresence>
        {userData?.spotify && (
          <div className="w-full lg:w-[320px] mt-4 lg:mt-0 lg:absolute lg:-bottom-8 lg:right-0">
            <SpotifyVisualizer spotify={userData.spotify} />
          </div>
        )}
      </AnimatePresence>

      {/* Preview modal - Rendered via Portal */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {isPreviewOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/95 backdrop-blur-2xl p-4 sm:p-4"
              onClick={() => setIsPreviewOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="relative max-w-[320px] w-full bg-white dark:bg-slate-900 rounded-xl p-4 shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-slate-100 dark:border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drag Handle for mobile */}
                <div className="w-12 h-1 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mb-4 sm:hidden opacity-50" />

                <div className="relative group aspect-square w-full overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800 shadow-inner border border-slate-200 dark:border-slate-800">
                  <img
                    src={`https://cdn.discordapp.com/avatars/${discord_user.id}/${discord_user.avatar}.png?size=1024`}
                    alt="Avatar Preview"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {avatarFrame && (
                    <img
                      src={avatarFrame}
                      alt="Avatar Frame"
                      className="absolute inset-0 w-full h-full z-20 pointer-events-none scale-[1.02]"
                    />
                  )}
                </div>

                {/* User info box at bottom */}
                <div className="mt-4 pb-2 text-center">
                  <p className="text-lg font-black text-slate-800 dark:text-white pb-1 tracking-tight transition-colors">
                    @{discord_user.username}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <p className="text-slate-500 dark:text-slate-400 text-[10px] uppercase font-bold tracking-[0.2em] transition-colors">Discord Profile Preview</p>
                  </div>
                </div>

                {/* Close Button Mobile/Desktop footer style */}
                <button
                  onClick={() => setIsPreviewOpen(false)}
                  className="mt-4 w-full py-2.5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 hover:border-slate-400 dark:bg-slate-800 dark:hover:bg-slate-750 dark:text-white dark:border-slate-700 font-bold rounded-lg active:scale-95 transition-all text-xs uppercase tracking-widest shadow-md"
                >
                  Close Preview
                </button>

                {/* Desktop Close Button */}
                <button
                  onClick={() => setIsPreviewOpen(false)}
                  className="hidden sm:flex absolute -top-3 -right-3 w-8 h-8 bg-white dark:bg-slate-800 shadow-xl rounded-lg items-center justify-center text-slate-800 dark:text-white font-bold z-50 border border-slate-100 dark:border-slate-800 hover:rotate-90 transition-all active:scale-90 text-xs"
                >
                  ✕
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

export default UserInfo;
